import styled, { css } from 'styled-components';
import { media, customMedia } from 'styles/utils';
import Button from 'components/Button';
import Heading from 'components/Heading';
import Paragraph from 'components/Paragraph';

import { conf } from './config.yaml';

const variantColor = {
  primary: 'wfWhite',
  secondary: 'wfBlack',
  tertiary: 'wfGrey100',
};

export const TagIntro = styled.small`
  font-size: ${props => props.theme.spacing.default};
  display: block;
`;

export const HeadingStyled = styled(Heading)`
  margin: 0;
  text-align: ${props => (props.textCenter ? 'center' : 'initial')};
  max-width: 820px;
  ${customMedia.lessThan('medium')`
    font-size: ${props => props.theme.font.size.xlarge};
  `}
`;

export const ParagraphStyled = styled(Paragraph)`
  text-align: ${props => (props.textCenter ? 'center' : 'initial')};
  margin-bottom: ${props => props.theme.spacing.default};
  max-width: 620px;
`;

export const ContentIntroStyled = styled.article`
  display: flex;
  justify-content: center;
  min-height: ${conf.pattern.size * conf.pattern.rows +
    conf.pattern.strokeWidth / 2}px;
  /* max-width: ${props => props.theme.layout.maxWidth}; */
  margin-left: auto;
  margin-right: auto;
  position: relative;
  margin-bottom: ${props =>
    props.marginBottom ? props.marginBottom : props.theme.spacing.medium};
  background-color: ${props =>
    props.theme.color[variantColor[props.variant]] ||
    props.theme.color.wfGrey100};
  ${HeadingStyled} {
    color: ${props =>
      props.variant === 'secondary' && props.theme.color.wfWhite};
  }
  ${ParagraphStyled} {
    ${props =>
      props.variant === 'secondary' &&
      css`
        color: ${props.theme.color.wfWhite};
        text-align: center;
        max-width: initial;
      `}
  }
`;

export const Container = styled.div`
  width: ${props => !props.isCentered && '100%'};
  max-width: ${props => props.theme.layout.maxWidth};
  padding: ${props =>
    props.variant === 'secondary'
      ? props.theme.layout.paddingInline
      : `${props.theme.spacing.xlarge} ${props.theme.layout.paddingInline}`};
  margin: ${props => (props.isCentered ? 'auto' : '0 auto')};
  text-align: ${props => props.variant === 'secondary' && 'center'};
  position: relative;
  z-index: 5;

  ${props =>
    props.textCenter &&
    css`
      display: flex;
      flex-direction: column;
    `}
`;

export const ButtonStyled = styled(Button)`
  margin-top: ${props => props.theme.spacing.default};
  margin-right: ${props => props.theme.spacing.large};
`;

export const GridWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto;
  margin-top: -${props => props.theme.spacing.xlarge};
  margin-bottom: ${props => props.theme.spacing.medium};
  > div {
    margin: 0;
    &:nth-child(-n + 3) {
      border-top: 0;
    }
    &:nth-of-type(3n + 1) {
      border-left: 0;
    }
    &:last-child {
      border-bottom: 0;
    }
    ${HeadingStyled}, ${TagIntro} {
      padding: ${props => props.theme.spacing.medium};
    }
    ${TagIntro} {
      padding-top: ${props => props.theme.spacing.medium};
    }
    ${ParagraphStyled} {
      padding-top: ${props => props.theme.spacing.medium};
      padding-left: ${props => props.theme.spacing.medium};
      margin-bottom: 0;
    }
  }
  /** Media queries */
  ${media.large`
    grid-template-columns: repeat(2, ${conf.grid.cellWidth}) auto;
  `}
  ${props =>
    props.backgroundLayer &&
    customMedia.greaterThan('medium')`
      display: none;
  `}
`;

export const RegualBlock = styled.div`
  ${props =>
    props.backgroundLayer &&
    customMedia.lessThan('medium')`
      display: none;
  `}
`;

export const GridCellStyled = styled.div`
  min-width: ${conf.grid.cellWidth};
  max-width: ${props => `calc(${props.theme.layout.maxWidth}/2)`};
  border-left: ${conf.grid.borderWidth} dashed
    ${props => props.theme.color.wfGrey300};
  border-top: ${conf.grid.borderWidth} dashed
    ${props => props.theme.color.wfGrey300};
  position: relative;
  /** Pseudo */
  &::before {
    content: ${props => props.polygon && `''`};
    position: absolute;
    width: 100%;
    height: 100%;
    background-color: ${props =>
      props.polygon &&
      (props.theme.color[props.polygonColor] || props.theme.color.wfGrey200)};
    clip-path: ${props =>
      props.polygon === 'triangle'
        ? 'polygon(100% 0, 0% 100%, 100% 100%)'
        : props.polygon === 'circle'
        ? 'circle(50% at 50% 50%)'
        : null};
  }
  /** Media queries */
  ${customMedia.lessThan('large')`
    &:empty {
      display: none;
    }
  `}
`;

export const HeroButtons = styled.nav`
  /** Media queries */
  ${media.large`
    margin-left: ${props =>
      props.isCentered
        ? 'auto'
        : `calc(${conf.grid.cellWidth} * 2 + ${props.theme.spacing.medium})`};
    margin-right: ${props => (props.isCentered ? 'auto' : 0)};
  `}
`;

export const Pattern = styled.svg`
  position: absolute;
  left: -1px;
  top: 0;
`;

export const PatternPath = styled.path`
  stroke: ${props =>
    props.variant === 'secondary'
      ? props.theme.color.wfGrey900
      : props.theme.color.wfGrey300};
  stroke-width: ${conf.pattern.strokeWidth};
`;

export const BackgroundLayerStyled = styled.div`
  position: absolute;
  z-index: 0;
  width: ${props => props.theme.layout.maxWidth};
  height: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-content: center;
  align-items: flex-start;
  padding: ${props => `0 ${props.theme.spacing.xlarge}`};

  ${customMedia.lessThan('medium')`
    display: none;
  `}
`;

export const Center = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-content: center;
  align-items: center;
`;
